import { useState, useRef, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useAgent, useSubagents } from "@/hooks/use-agents";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AgentTasksTab } from "@/components/agent/agent-tasks-tab";
import { AgentConfigTab } from "@/components/agent/agent-config-tab";
import { AgentAccessTab } from "@/components/agent/agent-access-tab";
import { AgentSubagentsTab } from "@/components/agent/agent-subagents-tab";
import { useTenant } from "@/context/tenant-context";
import { useAuth } from "@/context/auth-context";
import { useAgentAccess } from "@/hooks/use-agent-access";
import { AgentMigrateRuntimeModal } from "@/components/agent/agent-migrate-runtime";
import { azureApi } from "@/api/azure";
import type { AzureDeployment } from "@/api/azure";
import { agentsApi } from "@/api/agents";
import { queryKeys } from "@/lib/query-keys";
import { agentRoleLabel } from "@/lib/agent-display";
import { VM_SIZES, REGIONS } from "@/pages/tenant/new-agent/data";
import { Bot, Rocket, Cloud, Loader2, Globe, Terminal, Cpu, Webhook, Zap, HeartPulse, Layers, Sparkles, Pencil, CheckCircle2, AlertTriangle, ArrowRightLeft } from "lucide-react";
import { cn } from "@/lib/utils";

type Tab = "tasks" | "config" | "access" | "subagents";

// The chat surface lives at /tenants/:id/workspace — the agent detail
// page no longer renders its own chat tab. If a URL still has ?tab=chat
// the component redirects to the workspace (see redirect effect below).
const primaryTabs: { id: Tab; label: string }[] = [
  { id: "config", label: "Config" },
  { id: "tasks", label: "Tasks" },
];

const settingsTabs: { id: Tab; label: string }[] = [
  { id: "access", label: "Access" },
];

const statusVariant: Record<string, "success" | "warning" | "danger" | "default" | "secondary"> = {
  active: "success",
  running: "success",
  idle: "secondary",
  paused: "warning",
  pending_approval: "warning",
  error: "danger",
  terminated: "danger",
};

const deployStatusVariant: Record<string, "success" | "warning" | "danger" | "default" | "secondary"> = {
  running: "success",
  provisioning: "warning",
  pending: "warning",
  stopped: "secondary",
  deallocated: "secondary",
  failed: "danger",
  deleted: "danger",
};

const adapterIcons: Record<string, typeof Globe> = {
  openclaw_gateway: Globe,
  anthropic_native: Sparkles,
  claude_code: Terminal,
  process: Cpu,
  http: Webhook,
};

export function AgentDetail({
  agentId: embeddedAgentId,
  embedded = false,
}: {
  agentId?: string;
  embedded?: boolean;
}) {
  const { agentId: routeAgentId } = useParams<{ agentId: string }>();
  const agentId = embeddedAgentId ?? routeAgentId;
  const { tenantId, isAdmin } = useTenant();
  // Per-agent management gate — tenant admins always pass; agent admins
  // need an agent_access grant for *this* agent; standard users never
  // pass. Hides the settings cog + access tab entirely when false.
  const { canManage } = useAgentAccess(agentId);
  const { isInstanceAdmin } = useAuth();
  const [migrateModalOpen, setMigrateModalOpen] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: agent } = useAgent(agentId!);
  const { data: subagents = [] } = useSubagents(agentId!);
  const [searchParams, setSearchParams] = useSearchParams();
  const [embeddedTab, setEmbeddedTab] = useState<Tab>("config");
  const rawTab = embedded ? embeddedTab : searchParams.get("tab");
  // Default landing tab on the agent detail page is now Config. Chat has
  // moved to /workspace (single source of truth so two browser tabs stay
  // in sync on the same session). Users who want to chat click the Chat
  // icon in the sidebar / mobile bottom bar.
  const requestedTab: Tab = (rawTab === "tasks" || rawTab === "config" || rawTab === "access" || rawTab === "subagents")
    ? (rawTab as Tab)
    : "config";
  // Settings-only tabs collapse to "config" when the user can't manage —
  // e.g. an agent admin bookmarked ?tab=access on an agent they later
  // lost access to. The server would reject the underlying API calls
  // anyway; folding to config keeps the URL clean.
  const isSettingsTab = requestedTab === "access" || requestedTab === "subagents";
  const activeTab: Tab = isSettingsTab && !canManage ? "config" : requestedTab;

  // Legacy URL redirect: anything hitting /agents/:id?tab=chat (or with no
  // tab param at all, when we used to default to chat) goes to workspace.
  useEffect(() => {
    if (embedded) return;
    if (rawTab === "chat") {
      navigate(`/tenants/${tenantId}/workspace?agentId=${agentId}`, { replace: true });
    }
  }, [embedded, rawTab, agentId, tenantId, navigate]);
  const [showRetryDeploy, setShowRetryDeploy] = useState(false);
  const [retryForm, setRetryForm] = useState({ region: "uksouth", vmSize: "Standard_B2s" });

  // Fetch deployments for this agent — only poll during active provisioning
  const { data: deployments = [] } = useQuery({
    queryKey: queryKeys.deployments.byCompany(tenantId),
    queryFn: () => azureApi.listDeployments(tenantId),
    refetchInterval: (query) => {
      const deps = query.state.data as AzureDeployment[] | undefined;
      const hasProvisioning = deps?.some((d) => d.status === "provisioning" || d.status === "pending");
      return hasProvisioning ? 15000 : false;
    },
  });
  const agentDeployment = deployments.find((d: AzureDeployment) => d.agentId === agentId);
  const isProvisioning = agentDeployment?.status === "provisioning" || agentDeployment?.status === "pending";

  const [healthResult, setHealthResult] = useState<{
    reachable: boolean;
    gatewayUp?: boolean;
    sidecarUp?: boolean;
    error?: string;
  } | null>(null);

  const wakeMutation = useMutation({
    mutationFn: () => agentsApi.wake(agentId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.agents.detail(agentId!) });
    },
  });

  const healthMutation = useMutation({
    mutationFn: () => agentsApi.healthCheck(agentId!),
    onSuccess: (data) => {
      setHealthResult(data);
      setTimeout(() => setHealthResult(null), 10000);
    },
  });

  const isVmTier = agent?.runtimeTier === "vm";
  const isParentAgent = isVmTier && !agent?.parentAgentId;
  const isSubagent = !!agent?.parentAgentId;

  // Build the cog menu tabs
  const cogTabs = isParentAgent
    ? [...settingsTabs, { id: "subagents" as Tab, label: `Subagents${subagents.length ? ` (${subagents.length})` : ""}` }]
    : settingsTabs;

  const deployFailed = agentDeployment?.status === "failed";
  const noDeployment = isVmTier && !isSubagent && !agentDeployment;

  const retryDeployMutation = useMutation({
    mutationFn: () =>
      azureApi.createDeployment({
        companyId: tenantId,
        agentId: agentId!,
        name: agent?.name?.toLowerCase().replace(/\s+/g, "-") || agentId!.slice(0, 8),
        region: retryForm.region,
        vmSize: retryForm.vmSize,
      }),
    onSuccess: () => {
      setShowRetryDeploy(false);
      queryClient.invalidateQueries({ queryKey: queryKeys.deployments.byCompany(tenantId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.agents.detail(agentId!) });
    },
  });

  // ── Inline rename ──────────────────────────────────────────────
  // Admins edit the name in place on the header. Save → POST /rename
  // which updates the DB, rewrites the Peer Agents section of every
  // sibling's AGENTS.md, and best-effort pushes updated files to each
  // agent's VM via SSH. The per-agent push results come back in
  // propagation.pushes and are surfaced as a toast-style banner.
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameDraft, setRenameDraft] = useState("");
  const [renameResult, setRenameResult] = useState<
    | null
    | {
        oldName: string;
        newName: string;
        peersUpdated: number;
        pushes: Array<{ agentId: string; agentName: string; pushed: boolean; reason?: string }>;
      }
  >(null);
  const renameInputRef = useRef<HTMLInputElement>(null);

  const renameMutation = useMutation({
    mutationFn: (name: string) => agentsApi.rename(agentId!, name),
    onSuccess: (data) => {
      if (data.renamed) {
        setRenameResult({
          oldName: data.oldName ?? "",
          newName: data.newName ?? "",
          peersUpdated: data.propagation.peersUpdated,
          pushes: data.propagation.pushes,
        });
        setTimeout(() => setRenameResult(null), 12000);
      }
      setIsRenaming(false);
      queryClient.invalidateQueries({ queryKey: queryKeys.agents.detail(agentId!) });
      queryClient.invalidateQueries({ queryKey: queryKeys.agents.byCompany(tenantId) });
    },
  });

  const startRename = () => {
    setRenameDraft(agent?.name ?? "");
    setIsRenaming(true);
    setTimeout(() => renameInputRef.current?.select(), 0);
  };
  const commitRename = () => {
    const trimmed = renameDraft.trim();
    if (!trimmed || trimmed === agent?.name) {
      setIsRenaming(false);
      return;
    }
    renameMutation.mutate(trimmed);
  };
  const cancelRename = () => {
    setIsRenaming(false);
    setRenameDraft("");
  };
  const selectTab = (tab: Tab) => {
    if (embedded) {
      setEmbeddedTab(tab);
      return;
    }
    setSearchParams({ tab });
  };

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Agent header + tab bar */}
      <div className="shrink-0 border-b border-border bg-surface px-6 pt-4 pb-0 lg:pt-3 max-sm:px-3 max-sm:pt-2">
        <div className="mb-3 lg:mb-2 flex items-center gap-3 max-sm:hidden">
          {(() => {
            const AdapterIcon = (agent?.adapterType ? adapterIcons[agent.adapterType] : null) ?? Bot;
            return (
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10">
                <AdapterIcon size={20} className="text-accent" />
              </div>
            );
          })()}
          <div>
            {isRenaming ? (
              <div className="flex items-center gap-2">
                <input
                  ref={renameInputRef}
                  value={renameDraft}
                  onChange={(e) => setRenameDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") commitRename();
                    if (e.key === "Escape") cancelRename();
                  }}
                  disabled={renameMutation.isPending}
                  maxLength={100}
                  className="rounded-md border border-border bg-background px-2 py-1 text-lg font-semibold text-text-primary outline-none focus:border-accent"
                  autoFocus
                />
                <Button
                  size="sm"
                  onClick={commitRename}
                  disabled={renameMutation.isPending || !renameDraft.trim()}
                >
                  {renameMutation.isPending ? (
                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                  ) : null}
                  Save
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={cancelRename}
                  disabled={renameMutation.isPending}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="group flex items-center gap-1.5">
                <h1 className="text-lg font-semibold text-text-primary">
                  {agent?.name ?? "Loading..."}
                </h1>
                {canManage && agent && (
                  <button
                    onClick={startRename}
                    className="opacity-0 group-hover:opacity-100 rounded p-1 text-text-muted transition hover:bg-hover hover:text-text-primary"
                    title="Rename agent"
                  >
                    <Pencil size={13} />
                  </button>
                )}
              </div>
            )}
            <p className="text-xs text-text-muted">
              <span>{agent ? agentRoleLabel(agent) : ""}</span>
              {agent?.adapterType && agent.adapterType !== "process" && (
                <span> · {agent.adapterType === "openclaw_gateway" ? "OpenClaw" : agent.adapterType === "claude_code" ? "Claude Code" : agent.adapterType}</span>
              )}
              {agent?.adapterConfig?.model != null && (
                <span> · {String(agent.adapterConfig.model)}</span>
              )}
            </p>
          </div>
          {agent && (
            <Badge variant={statusVariant[agent.status] ?? "default"} className="ml-2">
              {agent.status}
            </Badge>
          )}
          {agentDeployment && (
            <Badge variant={deployStatusVariant[agentDeployment.status] ?? "default"}>
              {isProvisioning && <Loader2 size={12} className="mr-1 animate-spin" />}
              {agentDeployment.status}
              {agentDeployment.vmSize && ` · ${agentDeployment.vmSize}`}
            </Badge>
          )}
          {healthResult && (
            <Badge variant={healthResult.reachable ? "success" : "danger"}>
              {healthResult.reachable
                ? `Healthy${healthResult.gatewayUp ? " · GW" : ""}${healthResult.sidecarUp ? " · SC" : ""}`
                : healthResult.error || "Unreachable"}
            </Badge>
          )}
          {isSubagent && (
            <Badge variant="secondary" className="ml-1">
              <Layers size={10} className="mr-1" />
              Subagent
            </Badge>
          )}
          <div className="ml-auto flex items-center gap-2">
            {/* Management affordances are hidden for users who can't
                manage this agent (standard users, agent admins without
                a grant). The server also enforces these — UI just stops
                showing dead buttons. Deploy retry stays tenant_admin
                only since it provisions a VM. */}
            {canManage && isParentAgent && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  if (!embedded) navigate(`/tenants/${tenantId}/agents/${agentId}/subagents/new`);
                }}
                disabled={embedded}
                title={embedded ? "Create sub-agents from the full Agents page." : undefined}
              >
                <Layers size={14} className="mr-1.5" />
                Add Subagent
              </Button>
            )}
            {/* Platform-admin-only: in-place runtime migration.
                Top-level agents migrate the whole VM tree (OpenClaw ⇄ Native
                ⇄ ProClaw). Subagents can migrate INDIVIDUALLY to the ProClaw
                Runtime side-by-side — the parent + siblings stay on the
                OpenClaw gateway while this one moves to :18800. */}
            {isInstanceAdmin &&
              (isParentAgent
                ? agent?.adapterType === "openclaw_gateway" ||
                  agent?.adapterType === "anthropic_native"
                : agent?.adapterType === "openclaw_gateway") && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setMigrateModalOpen(true)}
                  title="Switch this agent between OpenClaw and Anthropic Native on the same VM"
                >
                  <ArrowRightLeft size={14} className="mr-1.5" />
                  Migrate runtime
                </Button>
              )}
            {canManage && (
              <Button
                size="sm"
                variant="outline"
                disabled={healthMutation.isPending}
                onClick={() => healthMutation.mutate()}
              >
                <HeartPulse size={14} className="mr-1.5" />
                {healthMutation.isPending ? "Checking..." : "Health Check"}
              </Button>
            )}
            {canManage && (
              <Button
                size="sm"
                variant="outline"
                disabled={wakeMutation.isPending}
                onClick={() => wakeMutation.mutate()}
              >
                <Zap size={14} className="mr-1.5" />
                {wakeMutation.isPending ? "Waking..." : "Wake"}
              </Button>
            )}
            {isAdmin && !isSubagent && (deployFailed || noDeployment) && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowRetryDeploy(!showRetryDeploy)}
              >
                <Rocket size={14} className="mr-1.5" />
                {deployFailed ? "Retry Deploy" : "Deploy"}
              </Button>
            )}
          </div>
        </div>

        {/* Deployment status card for VM-tier agents — desktop only */}
        {isVmTier && !showRetryDeploy && (
          <div className="max-sm:hidden">
            {isProvisioning && (
              <div className="mb-3 flex items-center gap-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
                <Loader2 size={16} className="text-amber-500 animate-spin shrink-0" />
                <div>
                  <p className="text-sm font-medium text-text-primary">Deploying to Azure...</p>
                  <p className="text-xs text-text-muted">Your agent VM is being provisioned. This typically takes 2-5 minutes.</p>
                </div>
              </div>
            )}
            {deployFailed && (
              <div className="mb-3 flex items-center gap-3 rounded-lg border border-red-500/30 bg-red-500/5 p-3">
                <Cloud size={16} className="text-red-500 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-text-primary">Deployment failed</p>
                  <p className="text-xs text-text-muted">
                    {agentDeployment?.metadata?.error
                      ? String(agentDeployment.metadata.error)
                      : "An error occurred during provisioning. Use Retry Deploy to try again."}
                  </p>
                </div>
              </div>
            )}
            {noDeployment && (
              <div className="mb-3 flex items-center gap-3 rounded-lg border border-border bg-background p-3">
                <Cloud size={16} className="text-text-muted shrink-0" />
                <div>
                  <p className="text-sm font-medium text-text-primary">Queued for deployment</p>
                  <p className="text-xs text-text-muted">This agent is being provisioned in Azure. The deployment will appear shortly.</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Retry deploy panel — desktop only */}
        {showRetryDeploy && (
          <div className="max-sm:hidden mb-3 flex items-center gap-3 rounded-lg border border-border bg-background p-3">
            <Cloud size={16} className="text-accent shrink-0" />
            <select
              value={retryForm.region}
              onChange={(e) => setRetryForm({ ...retryForm, region: e.target.value })}
              className="rounded-md border border-border bg-surface px-2 py-1.5 text-xs"
            >
              {REGIONS.map((r) => (
                <option key={r.id} value={r.id}>{r.label}</option>
              ))}
            </select>
            <select
              value={retryForm.vmSize}
              onChange={(e) => setRetryForm({ ...retryForm, vmSize: e.target.value })}
              className="rounded-md border border-border bg-surface px-2 py-1.5 text-xs"
            >
              {VM_SIZES.map((vm) => (
                <option key={vm.id} value={vm.id}>
                  {vm.label} ({vm.spec}) ~${vm.cost}/mo
                </option>
              ))}
            </select>
            <Button
              size="sm"
              disabled={retryDeployMutation.isPending}
              onClick={() => retryDeployMutation.mutate()}
            >
              {retryDeployMutation.isPending ? "Deploying..." : "Deploy to Azure"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setShowRetryDeploy(false)}>
              Cancel
            </Button>
            {retryDeployMutation.isError && (
              <span className="text-xs text-red-500">
                {(retryDeployMutation.error as Error).message}
              </span>
            )}
          </div>
        )}

        {/* Tabs: Config / Tasks / Access (+ Subagents for parent agents). The
            manage-only tabs used to hide behind a settings cog — they're now
            first-class tabs styled exactly like Config and Tasks. */}
        <div className="flex items-center">
          <div className="flex flex-1 justify-center gap-0 max-sm:justify-start max-sm:overflow-x-auto">
            {(canManage ? [...primaryTabs, ...cogTabs] : primaryTabs).map((tab) => (
              <button
                key={tab.id}
                onClick={() => selectTab(tab.id)}
                className={cn(
                  "relative px-5 py-2 text-[13px] font-medium transition-colors max-sm:px-3 max-sm:whitespace-nowrap",
                  activeTab === tab.id
                    ? "text-accent"
                    : "text-text-secondary hover:text-text-primary",
                )}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent" />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Rename result banner — auto-hides after ~12 s */}
      {renameResult && (
        <div className="shrink-0 border-b border-border bg-surface px-6 py-2.5 text-xs">
          {(() => {
            const ok = renameResult.pushes.every((p) => p.pushed);
            const failed = renameResult.pushes.filter((p) => !p.pushed);
            return (
              <div className={`flex items-start gap-2 ${ok ? "text-green-700 dark:text-green-400" : "text-amber-700 dark:text-amber-400"}`}>
                {ok ? (
                  <CheckCircle2 size={14} className="mt-0.5 shrink-0" />
                ) : (
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                )}
                <div>
                  Renamed <span className="font-medium">{renameResult.oldName}</span> →{" "}
                  <span className="font-medium">{renameResult.newName}</span>.
                  {" "}
                  DB + {renameResult.peersUpdated} peer workspace
                  {renameResult.peersUpdated === 1 ? "" : "s"} updated.
                  {ok ? (
                    <> All {renameResult.pushes.length} agent VM{renameResult.pushes.length === 1 ? "" : "s"} received the new files.</>
                  ) : failed.length === 0 ? null : (
                    <>
                      {" "}{failed.length} VM{failed.length === 1 ? "" : "s"} couldn't be reached:{" "}
                      {failed.map((f, i) => (
                        <span key={f.agentId}>
                          {i > 0 && ", "}
                          <span className="font-mono">{f.agentName}</span>{" "}
                          <span className="text-text-muted">({f.reason})</span>
                        </span>
                      ))}
                      . They'll pick up the new name on their next sync-workspace or next deploy.
                    </>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Tab content — chat lives at /workspace now */}
      <div className="min-h-0 flex-1 overflow-hidden">
        {activeTab === "tasks" && <AgentTasksTab agentId={agentId!} />}
        {activeTab === "config" && <AgentConfigTab agentId={agentId!} />}
        {activeTab === "access" && <AgentAccessTab agentId={agentId!} />}
        {activeTab === "subagents" && <AgentSubagentsTab parentAgentId={agentId!} />}
      </div>

      {migrateModalOpen && agent?.adapterType && (
        <AgentMigrateRuntimeModal
          agentId={agent.id}
          currentAdapter={agent.adapterType}
          isSubagent={!isParentAgent}
          onClose={() => setMigrateModalOpen(false)}
        />
      )}
    </div>
  );
}
